//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import ServiceRequestModel from './serviceRequest.model';

/**
 * @service ServiceRequestService
 * @description Service for managing Service Requests (SR) within the aquaculture system.
 * Service Requests are tickets raised for maintenance, repairs, or issues related to devices (sensors, aerators)
 * or general farm operations.
 *
 * This service provides the interface to retrieve these requests from the backend.
 * It interacts with the `/aqu/servicerequest/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **SR Retrieval**: Fetching service requests based on business context or date.
 * 2. **Dashboard Support**: Providing data for daily activity dashboards (e.g., "Today's Service Requests").
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve service requests for a specific business for the current date.
 * @feature 2. Retrieve all service requests for the current day (likely for admin/provider view).
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<ServiceRequestModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/servicerequest/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceRequestService extends Service<ServiceRequestModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/servicerequest/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/servicerequest/');
  }

  /**
   * @method getServiceRequestByBusinessId
   * @async
   * @description Fetch service requests associated with a specific business ID for the current date.
   *
   * Note: The current implementation constructs the URL as `currentDate/businessID/` without appending the `businessId` parameter value.
   * This implies the backend might infer the business context from the user's token, or this is a specific endpoint design.
   *
   * @param {string} businessId - The unique identifier of the business (passed but potentially unused in URL construction).
   * @returns {Promise<Array<ServiceRequestModel>>} Promise resolving to an array of ServiceRequestModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * serviceRequestService.getServiceRequestByBusinessId('bus-123').then(requests => {
   *   console.log('Today\'s Requests:', requests);
   * });
   */
  public getServiceRequestByBusinessId(businessId: string) {
    return new Promise<Array<ServiceRequestModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ServiceRequestModel>>(`${this.baseUrl}${this.serviceUrl}currentDate/businessID/`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCurrentDayServiceRequest
   * @async
   * @description Fetch all service requests for the current day.
   *
   * This is typically used for an aggregate view or dashboard to show all pending work for "today".
   *
   * @returns {Promise<Array<ServiceRequestModel>>} Promise resolving to an array of ServiceRequestModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `getAllSr/currentDate`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * serviceRequestService.getCurrentDayServiceRequest().then(requests => {
   *   console.log('All Today\'s Requests:', requests);
   * });
   */
  public getCurrentDayServiceRequest() {
    return new Promise<Array<ServiceRequestModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ServiceRequestModel>>(`${this.baseUrl}${this.serviceUrl}getAllSr/currentDate`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

   //To Cancel the onGoing Http Requests
   /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
   public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
